import React, { useState, useRef } from 'react';
import { 
  Button, 
  Typography, 
  CircularProgress, 
  Box,
  TextField,
  useMediaQuery, 
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import * as XLSX from 'xlsx';
import './App.css';
import translations from './localizations';

function SplitPage({ language }) {
  const [file, setFile] = useState(null);
  const [processingFile, setProcessingFile] = useState(false);
  const [data, setData] = useState(null);
  const [rowsPerFile, setRowsPerFile] = useState(1000);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const t = (key, params = {}) => {
    const translation = translations[language][key];
    if (typeof translation === 'string') {
      return Object.entries(params).reduce(
        (str, [key, value]) => str.replace(`{${key}}`, value),
        translation
      );
    }
    return key;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }
    setFile(file);
    setProcessingFile(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        setData(jsonData);
        setProcessingFile(false);
      } catch (error) {
        console.error('Error reading file:', error);
        setAlert({
          open: true,
          message: t('splitError'),
          severity: 'error'
        });
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleReset = () => {
    setFile(null);
    setData(null);
    setProcessingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const splitFile = async () => {
    if (!data) return;

    setProcessingFile(true);
    try {
      // Chia dữ liệu thành các phần nhỏ
      const chunks = [];
      for (let i = 0; i < data.length; i += rowsPerFile) {
        chunks.push(data.slice(i, i + rowsPerFile));
      }

      // Tạo và lưu các file
      const handle = await window.showDirectoryPicker();
      
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(chunk);
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        
        // Tạo tên file với số thứ tự
        const fileName = `${file.name.replace('.xlsx', '')}_part${i + 1}.xlsx`;
        
        // Chuyển workbook thành array buffer
        const wbout = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        
        // Tạo file mới trong thư mục đã chọn
        const fileHandle = await handle.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(wbout);
        await writable.close();
      }

      setAlert({
        open: true,
        message: t('splitSuccess', { count: chunks.length }),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error splitting file:', error);
      setAlert({
        open: true,
        message: t('splitError'),
        severity: 'error'
      });
    } finally {
      setProcessingFile(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '20px' }}>
        {t('splitTitle')}
      </Typography>
      
      <Box className="center-content">
        <div className="button-container">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
            ref={fileInputRef}
          />
          <label htmlFor="file-upload">
            <Button 
              variant="contained" 
              component="span" 
              disabled={processingFile} 
              className="custom-button"
            >
              {processingFile ? <CircularProgress size={24} /> : t('uploadButton')}
            </Button>
          </label>
          {file && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              className="reset-button custom-button"
            >
              {t('resetButton')}
            </Button>
          )}
        </div>
        
        {file && !processingFile && (
          <>
            <Typography variant="body1" gutterBottom align="center">
              {t('fileUploaded')} {file.name}
            </Typography>
            
            <Box mt={3} mb={3}>
              <TextField
                type="number"
                label={t('rowsPerFile')}
                value={rowsPerFile}
                onChange={(e) => setRowsPerFile(Math.max(1, parseInt(e.target.value) || 1000))}
                variant="outlined"
                size="small"
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={splitFile}
              disabled={processingFile}
            >
              {processingFile ? <CircularProgress size={24} /> : t('splitButton')}
            </Button>
          </>
        )}
      </Box>

      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SplitPage;
